import { storeWrapper } from '../storeWrapper'
import { ospreyAPI } from '@repo/api/ospreyAPI'
import { useWizardStore } from '../useWizardStore/useWizardStore'
import { throttle } from '@repo/utils/debounce'

const initialState = {
  partyIds: [],
  currentPartyId: null,
  parties: {},
  acceptanceStatus: false,
  acceptanceNotes: '',
  acceptanceTimestamp: null,
  totalRowCount: 0
}

export const useCOISStore = storeWrapper('useCOISStore', (set, get) => ({
  ...initialState,
  updateState: ({ key, value }) => set({ [key]: value }),
  addParty: ({ partyId }) => {
    const partyIds = get().partyIds
    const parties = get().parties

    if (!partyIds.includes(partyId)) {
      partyIds.push(partyId)
    }

    delete parties[partyId]
    const tempPartyIds = []
    set({ parties, partyIds: tempPartyIds })

    const party = { id: partyId }
    parties[partyId] = party
    set({ partyIds, parties })
    get().selectParty({ partyId })
  },
  updateParty: ({ partyId, newFieldKey, newFieldValue }) => {
    const parties = get().parties
    const party = parties[partyId]

    if (!parties || !party) return

    party[newFieldKey] = newFieldValue

    // delete existing to avoid shallow merge issues
    delete parties[partyId]
    set({ parties })

    // now reset parties with new value
    parties[partyId] = party
    set({ parties })

    // update currentParty value
    get().selectParty({ partyId })
  },
  removeParty: ({ partyId }) => {
    const parties = get().parties
    const partyIds = get().partyIds

    const tempParties = { ...parties }
    delete tempParties[partyId]
    const tempPartyIds = partyIds.filter((id) => id !== partyId)

    set({ parties: tempParties, partyIds: tempPartyIds })
  },
  selectParty: ({ partyId }) => {
    set({ currentPartyId: partyId })
  },
  fetchConflictsOfInterest: async ({ startAtIndex, pageSize }) => {
    return new Promise((resolve) => {
      throttle({
        id: `fetchConflictsOfInterest`,
        callback: async () => {
          const { parties, currentPartyId } = get()
          const partyData = parties[currentPartyId]

          const {
            forename,
            surname,
            companyName,
            emailAddress,
            phone1,
            phone2,
            house,
            area,
            county,
            postalTown,
            postcode,
            partyType,
            customDataPageFieldClauses
          } = partyData || {}

          const clauses = customDataPageFieldClauses ? [...customDataPageFieldClauses] : []
          // clean up data

          const requestData = {
            startAtIndex: startAtIndex || 0,
            pageSize: pageSize || 10,
            forename: partyType === 'individual' && forename ? forename : null,
            surname: partyType === 'individual' && surname ? surname : null,
            companyName: partyType === 'company' && companyName ? companyName : null,
            emailAddress: emailAddress || null,
            phoneNumbers: [phone1, phone2].filter((phone) => !!phone),
            house: house || null,
            area: area || null,
            county: county || null,
            postalTown: postalTown || null,
            postcode: postcode || null,
            customDataPageFieldClauses: clauses
              .map((clause) => {
                const comparison = clause.customDataPageFieldComparison
                if (!comparison.value) return null

                // this is a bit long, but we're trying to omit some data which is used to track state
                return {
                  customDataComparisonBooleanOperator: clause.customDataComparisonBooleanOperator,
                  customDataPageFieldComparison: {
                    customDataPageId: comparison.customDataPageId,
                    customDataFieldId: comparison.customDataFieldId,
                    comparisonOperator: comparison.comparisonOperator,
                    value: comparison.value
                  }
                }
              })
              .filter(Boolean)
          }

          // send request
          const response = await ospreyAPI({
            service: 'matters',
            endpoint: 'Matter/GetConflictsOfInterest',
            method: 'POST',
            data: requestData
          })

          set({ totalRowCount: response?.totalRowCount })

          const responseWithIds =
            response?.rows.map((conflict, index) => {
              conflict.id = `${Date.now()}-${index}`
              return conflict
            }) || []

          resolve(responseWithIds)
        }
      })
    })
  },
  updateCOISWizardSidebar: () => {
    const { parties, currentPartyId, acceptanceStatus } = get()
    const currentParty = parties[currentPartyId] || null

    const displayName = currentParty?.displayName || 'New party'
    const partyCount = Object.keys(parties).length
    const conflicts = currentParty?.conflicts || null
    const conflictCount = conflicts?.length || 0
    const conflictSingularOrPlural = conflictCount === 1 ? '' : 's'
    const rulesAdded = currentParty?.customDataPageFieldClauses || false
    let ruleCount = 0
    currentParty?.customDataPageFieldClauses?.forEach((clause) => {
      const { value } = clause.customDataPageFieldComparison
      if (value || value === 0 || value === '0' || value === false || value === 'false') {
        ruleCount++
      }
    })

    const ruleSingularOrPlural = ruleCount === 1 ? '' : 's'
    const addedAllParties = currentParty?.addedAllParties || false
    const accepted = acceptanceStatus || false
    const partySingularOrPlural = partyCount === 2 ? '' : 's'
    const additionalPartiesText = partyCount > 1 ? ` + ${partyCount - 1} other${partySingularOrPlural}` : ''

    // update wizard state
    const changes = [
      {
        key: `cois-introduction`,
        value: { status: currentParty ? 'completed' : 'todo', statusText: currentParty ? 'Search started' : '' }
      },
      {
        key: `cois-party-details`,
        value: {
          status: partyCount ? 'completed' : 'todo',
          statusText: partyCount ? `${displayName}${additionalPartiesText}` : ''
        }
      },
      {
        key: `cois-refine-your-search`,
        value: {
          status: rulesAdded ? 'completed' : 'todo',
          statusText: rulesAdded ? `${ruleCount} rule${ruleSingularOrPlural} added` : ''
        }
      },
      {
        key: `cois-possible-conflicts`,
        value: {
          status: conflicts ? 'completed' : 'todo',
          statusText: conflicts ? `${conflictCount} possible conflict${conflictSingularOrPlural}` : ''
        }
      },
      {
        key: `cois-review-parties`,
        value: { status: addedAllParties ? 'completed' : 'todo', statusText: addedAllParties ? 'Complete' : '' }
      },
      {
        key: `cois-confirm-search`,
        value: { status: accepted ? 'completed' : 'todo', statusText: accepted ? 'Acknowledged' : '' }
      }
    ]

    // debugger

    useWizardStore.getState().setWizardStateInBulk(changes)
  },
  getConflictOfInterestPartyRequestAndResponses: () => {
    const { parties } = get()

    return Object.values(parties)?.map((party) => {
      const updatedParty = {
        ...party,
        phoneNumbers: [party.phone1, party.phone2].filter((phone) => !!phone)
      }

      delete updatedParty.phone1
      delete updatedParty.phone2
      delete updatedParty.id
      delete updatedParty.partyType
      delete updatedParty.displayName
      delete updatedParty.addedAllParties
      delete updatedParty.conflicts

      updatedParty.customDataPageFieldClauses =
        updatedParty?.customDataPageFieldClauses
          ?.map((clause) => {
            const updatedClause = clause
            delete updatedClause.customDataPageFieldComparison.id
            delete updatedClause.customDataPageFieldComparison.ruleDefault
            delete updatedClause.customDataPageFieldComparison.fieldDefault
            delete updatedClause.customDataPageFieldComparison.sourceDefault
            delete updatedClause.customDataPageFieldComparison.valueDefault
            return updatedClause
          })
          .filter((clause) => {
            return clause.customDataPageFieldComparison.value
          }) || []

      return {
        conflictOfInterestPartyRequest: updatedParty,
        conflictsOfInterest:
          party?.conflicts?.map((conflict) => {
            const updatedConflict = conflict
            delete updatedConflict.id
            return updatedConflict
          }) || []
      }
    })
  },
  getConflictOfInterestAcknowledgementPayload: () => {
    const { acceptanceNotes, acceptanceTimestamp } = get()

    return {
      notes: acceptanceNotes || null,
      dateTime: acceptanceTimestamp || null
    }
  },
  reset: () => {
    const tempParties = { ...get().parties }

    Object.keys(tempParties).forEach((partyId) => {
      delete tempParties[partyId]
    })

    let tempPartyIds = []

    set({
      partyIds: tempPartyIds,
      currentPartyId: null,
      currentParty: null,
      parties: tempParties,
      acceptanceStatus: false,
      acceptanceNotes: '',
      acceptanceTimestamp: null
    })
  }
}))
